
import { useEffect, useMemo, useRef } from 'react';
import { useTemplateLoading } from './useTemplateLoading';
import { useTemplateForm } from './useTemplateForm';
import { useSystemVariables } from './useSystemVariables';
import { useHtmlPreview } from './useHtmlPreview';

export function useTemplateEditor(templateId: string) {
  // Keep a reference to the template ID to detect changes
  const prevTemplateIdRef = useRef<string | null>(null);
  
  // Destructure and compose the various hooks
  const {
    loading,
    template,
    error,
    loadTemplate,
    isMountedRef,
    authReady
  } = useTemplateLoading(templateId);
  
  const {
    systemVariables,
    loadSystemVariables
  } = useSystemVariables();
  
  const {
    editedTemplate,
    previewHtml,
    variablesJson,
    variablesValid,
    loadingAction,
    handleInputChange,
    handleSelectChange,
    handleVariablesChange,
    handleSave
  } = useTemplateForm(templateId, template, isMountedRef);
  
  const {
    getPreviewHtmlWithExampleValues
  } = useHtmlPreview({
    systemVariables,
    variablesJson,
    variablesValid
  });
  
  // Control template loading with proper dependency tracking and prevent infinite loops
  useEffect(() => {
    if (!isMountedRef.current || !authReady) return;
    
    // Only load if the template ID has changed or it's the first load
    const isFirstLoad = prevTemplateIdRef.current === null;
    const hasTemplateChanged = prevTemplateIdRef.current !== templateId;
    
    if (isFirstLoad || hasTemplateChanged) {
      console.log(`Template ID changed from ${prevTemplateIdRef.current} to ${templateId}, loading new template.`);
      prevTemplateIdRef.current = templateId;
      
      // Use a timeout to ensure we don't have multiple consecutive loads
      const loadTimer = setTimeout(() => {
        if (isMountedRef.current) {
          loadTemplate();
          loadSystemVariables();
        }
      }, 50);
      
      return () => clearTimeout(loadTimer);
    }
  }, [templateId, loadTemplate, loadSystemVariables, isMountedRef, authReady]);
  
  // Use memo for the return values to prevent unnecessary re-renders
  return useMemo(() => ({
    template,
    editedTemplate,
    loading,
    loadingAction,
    previewHtml,
    variablesJson,
    variablesValid,
    handleInputChange,
    handleSelectChange,
    handleVariablesChange,
    handleSave,
    getPreviewHtmlWithExampleValues,
    error,
    authReady
  }), [
    template, 
    editedTemplate, 
    loading, 
    loadingAction, 
    previewHtml,
    variablesJson, 
    variablesValid,
    handleInputChange,
    handleSelectChange,
    handleVariablesChange,
    handleSave,
    getPreviewHtmlWithExampleValues,
    error,
    authReady
  ]);
}
